import React, { Component } from "react";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import debounce from "lodash/debounce";
import { Table, Button, Spin, Typography, Input, Select, Flex } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { controller } from "../../controller";
import DashboardLayout from "../../layout/dashboardLayout/DashboardLayout";
import searchAffixIcon from "../../assets/icons/input-affix.jpg";
import "../addProvider/style.css";
import "./OfficeProviders.css";
import "../officeAdminPage.css";

const { Title } = Typography;

const SORT_OPTIONS = [
  { value: "name", label: "Name (A–Z)" },
  { value: "-name", label: "Name (Z–A)" },
  { value: "status", label: "Status (Active first)" },
  { value: "-status", label: "Status (Inactive first)" },
];

function providerDisplayName(record) {
  if (record.firstname && record.lastname) return `${record.firstname} ${record.lastname}`;
  if (record.firstname) return record.firstname;
  if (record.lastname) return record.lastname;
  return record.name || "—";
}

class OfficeProviders extends Component {
  state = {
    serverLogo: "",
    loading: true,
    providers: [],
    search: "",
    ordering: "name",
  };

  async componentDidMount() {
    this.debouncedSearchLoad = debounce(() => this.loadProviders(), 400);
    const logo = await controller.getLogo();
    this.setState({ serverLogo: logo.data && logo.data.dark ? logo.data.dark : "" });
    await this.loadProviders();
  }

  componentWillUnmount() {
    if (this.debouncedSearchLoad && this.debouncedSearchLoad.cancel) {
      this.debouncedSearchLoad.cancel();
    }
  }

  loadProviders = async () => {
    this.setState({ loading: true });
    const office = localStorage.getItem("selectedOffice");
    const { search, ordering } = this.state;
    const response = await controller.get_provider(office, null, {
      search,
      ordering,
    });
    const raw = response.data || [];
    const providers = Array.isArray(raw) ? raw.filter((p) => !p.is_delete) : [];
    this.setState({ providers, loading: false });
  };

  onSearchChange = (e) => {
    const search = e && e.target ? e.target.value : "";
    this.setState({ search }, () => this.debouncedSearchLoad && this.debouncedSearchLoad());
  };

  onSortChange = (ordering) => {
    this.setState({ ordering }, () => this.loadProviders());
  };

  render() {
    const { profileSummary } = this.props;
    const columns = [
      {
        title: "Name",
        key: "name",
        width: "16%",
        ellipsis: true,
        render: (_, record) => providerDisplayName(record),
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        width: "20%",
        ellipsis: true,
        render: (v) => (v != null && v !== "" ? v : "—"),
      },
      {
        title: "Phone",
        dataIndex: "phone",
        key: "phone",
        width: "12%",
        ellipsis: true,
        render: (v) => (v != null && v !== "" ? v : "—"),
      },
      {
        title: "Specialty",
        dataIndex: "specialty",
        key: "specialty",
        width: "10%",
        ellipsis: true,
        render: (v) => (v != null && v !== "" ? v : "—"),
      },
      {
        title: "License #",
        dataIndex: "license_number",
        key: "license_number",
        width: "12%",
        ellipsis: true,
        render: (v) => (v != null && v !== "" ? v : "—"),
      },
      {
        title: "Unique #",
        dataIndex: "unique_provider_number",
        key: "unique_provider_number",
        width: "14%",
        ellipsis: true,
        render: (v) => (v != null && v !== "" ? v : "—"),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: "12%",
        render: (v) => (v != null ? String(v) : "—"),
      },
      {
        title: "",
        key: "actions",
        width: 140,
        render: (_, record) => (
          <Button
            type="primary"
            icon={<EditOutlined />}
            className="btn-provider"
            onClick={() => this.props.dispatch(push(`/office/providers/${record.id}/edit`))}
          >
            Edit
          </Button>
        ),
      },
    ];

    return (
      <DashboardLayout
        logo={profileSummary && profileSummary.logo ? profileSummary.logo : ""}
        footerLogo={this.state.serverLogo}
        breadCrumb={"Providers"}
      >
        <div className="paymentRequestContent office-providers-page">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "24px 0 0", flexWrap: "wrap", gap: 12 }}>
            <Title level={3} style={{ margin: 0 }}>
              Providers
            </Title>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              className="btn-provider"
              onClick={() => this.props.dispatch(push("/add-provider"))}
            >
              Add provider
            </Button>
          </div>
          <div className="office-providers-filters segments-and-filters" style={{ margin: "16px 0 0" }}>
            <Flex align="center" gap={10} wrap="wrap" style={{ width: "100%" }}>
              <Input
                allowClear
                placeholder="Search by name, email, phone, or ID numbers"
                value={this.state.search}
                onChange={this.onSearchChange}
                onPressEnter={() => this.loadProviders()}
                prefix={<img src={searchAffixIcon} alt="" />}
                style={{
                  width: 320,
                  maxWidth: "100%",
                  height: 40,
                  border: "1px solid #6B43B5",
                  borderRadius: 100,
                  padding: "0 11px",
                }}
                className="treatment-filter-input"
              />
              <Select
                size="large"
                options={SORT_OPTIONS}
                className="treatment-filter-input"
                style={{ width: 240, maxWidth: "100%" }}
                value={this.state.ordering}
                onChange={this.onSortChange}
                aria-label="Sort providers"
              />
            </Flex>
          </div>
          <Spin spinning={this.state.loading}>
            <div className="office-admin-card office-providers-table-wrap">
              <Table
                rowKey="id"
                className="office-providers-table"
                columns={columns}
                dataSource={this.state.providers}
                pagination={false}
                tableLayout="fixed"
                locale={{ emptyText: this.state.loading ? " " : "No providers found" }}
              />
            </div>
          </Spin>
        </div>
      </DashboardLayout>
    );
  }
}

function mapStateToProps(state) {
  const { profileSummary } = state.dashboard;
  return { profileSummary };
}

export default connect(mapStateToProps)(OfficeProviders);
